/**
 * Reconnecting WebSocket client (ARCHITECTURE §7).
 *
 * Shared transport infra used by BOTH the dashboard (`/ws/agent`) and the widget
 * (`/ws/widget`). This is a REAL, working generic client — not a stub. It
 * provides:
 *   - exponential backoff + full jitter reconnection ({@link Backoff}),
 *   - heartbeat ping/pong liveness detection ({@link Heartbeat}),
 *   - resume-since-lastEventId (tracks the last processed server event id and
 *     replays it on reconnect via a `resume` control frame),
 *   - an offline outbound queue (frames sent while disconnected are buffered and
 *     flushed on reconnect).
 *
 * It is intentionally domain-agnostic: it speaks {@link WsFrame} (versioned
 * envelopes + control frames) and hands decoded domain events to listeners. The
 * apps layer on top (e.g. the widget's `useWidgetSocket`, the dashboard's socket
 * hook) subscribe to typed events.
 */

import {
  WS_PROTOCOL_VERSION,
  isWsEvent,
  type AnyWsEnvelope,
  type WsControlFrame,
  type WsEventPayloadMap,
  type WsEventType,
  type WsFrame,
} from '../types/ws';
import { Backoff, type BackoffOptions } from './backoff';
import { Heartbeat } from './heartbeat';

/** Connection lifecycle states surfaced to consumers. */
export type WsConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed';

/** Listener for a single domain event type. */
export type WsEventListener<T extends WsEventType> = (
  payload: WsEventPayloadMap[T],
  envelope: AnyWsEnvelope,
) => void;

/** Listener for connection-state transitions. */
export type WsStateListener = (state: WsConnectionState) => void;

export interface WsClientOptions {
  /**
   * Resolves the full socket URL to connect to. Async so the app can mint/attach
   * a token (agent JWT or widget session token) as a query param each attempt.
   */
  url: () => string | Promise<string>;
  /** Backoff tuning for reconnection. */
  backoff?: BackoffOptions;
  /** Heartbeat interval/timeout overrides. */
  heartbeat?: { intervalMs?: number; timeoutMs?: number };
  /** Max queued outbound frames while offline (drops oldest beyond). Default 100. */
  maxQueue?: number;
  /**
   * Injectable WebSocket constructor (defaults to the global). Lets tests and
   * non-browser runtimes supply an implementation.
   */
  socketFactory?: (url: string) => WebSocket;
  /** Optional logger; defaults to no-op. */
  onError?: (error: unknown) => void;
}

/** A subscription handle; call to unsubscribe. */
export type Unsubscribe = () => void;

export class WsClient {
  private readonly opts: WsClientOptions;
  private readonly backoff: Backoff;
  private readonly heartbeat: Heartbeat;
  private readonly maxQueue: number;

  private socket: WebSocket | null = null;
  private state: WsConnectionState = 'idle';
  private manuallyClosed = false;

  /** Last server-assigned event id we successfully processed (for resume). */
  private lastEventId: string | null = null;

  /** Outbound frames buffered while the socket is not open. */
  private readonly outbox: WsFrame[] = [];

  /** Domain-event listeners keyed by event type. */
  private readonly eventListeners = new Map<WsEventType, Set<WsEventListener<WsEventType>>>();
  /** Connection-state listeners. */
  private readonly stateListeners = new Set<WsStateListener>();

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: WsClientOptions) {
    this.opts = options;
    this.maxQueue = options.maxQueue ?? 100;
    this.backoff = new Backoff(options.backoff);
    this.heartbeat = new Heartbeat({
      intervalMs: options.heartbeat?.intervalMs,
      timeoutMs: options.heartbeat?.timeoutMs,
      onPing: () => this.sendControl({ type: 'ping' }),
      // A dead heartbeat forces a reconnect (drop + backoff).
      onTimeout: () => this.forceReconnect('heartbeat-timeout'),
    });
  }

  /** Current connection state. */
  get connectionState(): WsConnectionState {
    return this.state;
  }

  /** Open (or re-open) the connection. Safe to call multiple times. */
  connect(): void {
    this.manuallyClosed = false;
    if (this.state === 'open' || this.state === 'connecting') return;
    void this.openSocket();
  }

  /** Permanently close; stops reconnection and clears timers. */
  close(): void {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.heartbeat.stop();
    if (this.socket) {
      try {
        this.socket.close(1000, 'client-close');
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
    this.setState('closed');
  }

  /**
   * Subscribe to a domain event type. Returns an unsubscribe function.
   * Typed so listeners receive the correct payload for the event.
   */
  on<T extends WsEventType>(type: T, listener: WsEventListener<T>): Unsubscribe {
    let set = this.eventListeners.get(type);
    if (!set) {
      set = new Set();
      this.eventListeners.set(type, set);
    }
    set.add(listener as WsEventListener<WsEventType>);
    return () => {
      set?.delete(listener as WsEventListener<WsEventType>);
    };
  }

  /** Subscribe to connection-state changes. Returns an unsubscribe function. */
  onStateChange(listener: WsStateListener): Unsubscribe {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  /**
   * Send a domain envelope. If the socket is not open, the frame is queued and
   * flushed on the next successful connection (offline outbound queue).
   */
  send(frame: WsFrame): void {
    if (this.socket && this.state === 'open' && this.socket.readyState === WebSocket.OPEN) {
      this.rawSend(frame);
    } else {
      this.enqueue(frame);
    }
  }

  // ---- internals ----

  private async openSocket(): Promise<void> {
    this.setState(this.backoff.attempts > 0 ? 'reconnecting' : 'connecting');
    let url: string;
    try {
      url = await this.opts.url();
    } catch (error) {
      this.opts.onError?.(error);
      this.scheduleReconnect();
      return;
    }

    let socket: WebSocket;
    try {
      socket = this.opts.socketFactory
        ? this.opts.socketFactory(url)
        : new WebSocket(url);
    } catch (error) {
      this.opts.onError?.(error);
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;
    socket.onopen = () => this.handleOpen();
    socket.onmessage = (ev) => this.handleMessage(ev.data);
    socket.onerror = (ev) => this.opts.onError?.(ev);
    socket.onclose = () => this.handleClose();
  }

  private handleOpen(): void {
    this.backoff.reset();
    this.setState('open');
    this.heartbeat.start();
    // Resume: tell the server the last event we processed so it can replay the gap.
    if (this.lastEventId) {
      this.rawSend({
        v: WS_PROTOCOL_VERSION,
        type: 'resume',
        lastEventId: this.lastEventId,
      });
    } else {
      this.rawSend({ v: WS_PROTOCOL_VERSION, type: 'hello' });
    }
    this.flushOutbox();
  }

  private handleMessage(raw: unknown): void {
    // Any inbound traffic proves liveness.
    this.heartbeat.notifyActivity();

    const frame = this.decode(raw);
    if (!frame) return;

    // Control frames are handled by the transport, not surfaced to the app.
    if (!isWsEvent(frame)) {
      // ping/pong/ack/error handled implicitly; activity already recorded above.
      // TODO(phase 3): surface `error` control frames to the app if actionable.
      return;
    }

    // Track resume position and dispatch to typed listeners.
    if (frame.id) this.lastEventId = frame.id;
    this.dispatch(frame);
  }

  private handleClose(): void {
    this.heartbeat.stop();
    this.socket = null;
    if (this.manuallyClosed) {
      this.setState('closed');
      return;
    }
    this.scheduleReconnect();
  }

  private forceReconnect(_reason: string): void {
    if (this.socket) {
      try {
        this.socket.close(4000, _reason);
      } catch {
        /* ignore */
      }
    }
    // handleClose (via onclose) will schedule the reconnect.
  }

  private scheduleReconnect(): void {
    if (this.manuallyClosed) return;
    this.setState('reconnecting');
    const delay = this.backoff.next();
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.openSocket();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private dispatch(frame: AnyWsEnvelope): void {
    const listeners = this.eventListeners.get(frame.type);
    if (!listeners) return;
    for (const listener of listeners) {
      try {
        listener(frame.data, frame);
      } catch (error) {
        this.opts.onError?.(error);
      }
    }
  }

  private enqueue(frame: WsFrame): void {
    // Drop oldest beyond the cap to bound memory (offline queue backpressure).
    if (this.outbox.length >= this.maxQueue) this.outbox.shift();
    this.outbox.push(frame);
  }

  private flushOutbox(): void {
    while (this.outbox.length > 0) {
      const frame = this.outbox.shift();
      if (frame) this.rawSend(frame);
    }
  }

  private rawSend(frame: WsFrame): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.enqueue(frame);
      return;
    }
    try {
      this.socket.send(JSON.stringify(frame));
    } catch (error) {
      this.opts.onError?.(error);
      this.enqueue(frame);
    }
  }

  private sendControl(partial: Pick<WsControlFrame, 'type'> & Partial<WsControlFrame>): void {
    this.rawSend({ v: WS_PROTOCOL_VERSION, ...partial });
  }

  private decode(raw: unknown): WsFrame | null {
    if (typeof raw !== 'string') return null;
    try {
      const parsed = JSON.parse(raw) as WsFrame;
      if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
        return null;
      }
      return parsed;
    } catch (error) {
      this.opts.onError?.(error);
      return null;
    }
  }

  private setState(next: WsConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.stateListeners) {
      try {
        listener(next);
      } catch (error) {
        this.opts.onError?.(error);
      }
    }
  }
}
